// src/components/inputs/SearchableMultiSelect.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Generic, dependency-free searchable multi-select with "All" option.
 */
export default function SearchableMultiSelect({
  options, // [{value, label}]
  values, // ['ALL', 'Labrador', ...]
  onChange, // (nextArray) => void
  placeholder = 'Type to search…',
  showAllOption = true,
  allValue = 'ALL',
  allLabel = 'All',
  maxVisible = 50,
}) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const normalized = useMemo(() => {
    const map = new Map();
    (options || []).forEach((o) => {
      if (!o?.value) return;
      map.set(String(o.value), {
        value: String(o.value),
        label: o.label || String(o.value),
      });
    });
    return Array.from(map.values());
  }, [options]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    let list = term
      ? normalized.filter((o) => o.label.toLowerCase().includes(term))
      : normalized.slice(0, maxVisible);
    const selectedSet = new Set(values);
    list = list.filter((o) => !selectedSet.has(o.value));
    if (showAllOption && (!term || allLabel.toLowerCase().includes(term))) {
      if (!selectedSet.has(allValue))
        list = [{ value: allValue, label: allLabel }, ...list];
    }
    return list.slice(0, maxVisible);
  }, [q, normalized, values, showAllOption, allValue, allLabel, maxVisible]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const commitAdd = (opt) => {
    if (!opt) return;
    if (opt.value === allValue) {
      onChange([allValue]); // selecting All clears others
    } else {
      const next = (values[0] === allValue ? [] : values).concat(opt.value);
      onChange(Array.from(new Set(next)));
    }
    setQ('');
    setOpen(true);
    setHighlight(0);
    inputRef.current?.focus();
  };

  const removeValue = (val) => onChange(values.filter((v) => v !== val));

  const onKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) setOpen(true);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, Math.max(filtered.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      commitAdd(filtered[highlight]);
    } else if (e.key === 'Backspace' && !q) {
      if (values.length) removeValue(values[values.length - 1]);
    }
  };

  return (
    <div
      ref={rootRef}
      className='form-control p-2'
      style={{ position: 'relative' }}
    >
      <div className='d-flex flex-wrap gap-2'>
        {values.map((v) => (
          <span key={v} className='badge bg-light text-dark border'>
            {v === allValue
              ? allLabel
              : normalized.find((o) => o.value === v)?.label || v}
            <button
              type='button'
              className='btn btn-sm btn-link text-decoration-none ms-1 p-0'
              onClick={() => removeValue(v)}
              aria-label='Remove'
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          style={{ minWidth: 160, border: 'none', outline: 'none', flex: 1 }}
        />
      </div>

      {open && (
        <div
          className='bg-white border rounded shadow-sm mt-2'
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            zIndex: 1000,
            maxHeight: 260,
            overflowY: 'auto',
          }}
        >
          {filtered.length === 0 ? (
            <div className='px-3 py-2 text-muted'>No matches</div>
          ) : (
            filtered.map((o, i) => (
              <button
                key={o.value}
                type='button'
                className={`dropdown-item text-start ${
                  i === highlight ? 'active' : ''
                }`}
                onMouseEnter={() => setHighlight(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => commitAdd(o)}
              >
                {o.label}
                {o.value === allValue && (
                  <span className='ms-2 badge bg-secondary'>All</span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
